import { Link } from "wouter";
import type { ReactNode } from "react";
import type { NotificationDTO } from "@shared/types/notifications";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/** Compact relative time ("just now", "5 minutes ago", or a short date). */
function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = then - Date.now();
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (abs < MINUTE) return "just now";
  if (abs < HOUR) return rtf.format(Math.round(diff / MINUTE), "minute");
  if (abs < DAY) return rtf.format(Math.round(diff / HOUR), "hour");
  if (abs < WEEK) return rtf.format(Math.round(diff / DAY), "day");
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface NotificationItemProps {
  notification: NotificationDTO;
  onMarkRead: (id: string) => void;
}

export function NotificationItem({ notification, onMarkRead }: NotificationItemProps) {
  const { id, title, body, linkPath, readAt, createdAt } = notification;
  const isUnread = !readAt;

  const activate = () => {
    if (isUnread) onMarkRead(id);
  };

  // Glass rows (docs/v2-core/STYLE.md): unread ones are brighter, with a sky
  // halo and the dot; read ones fade back toward the wash. No bordered boxes.
  const rowClass = cn(
    "flex w-full items-start gap-3 rounded-2xl px-4 py-3 text-left transition",
    isUnread
      ? "bg-white shadow-soft ring-1 ring-sky-100 hover:ring-sky-200"
      : "bg-white/55 ring-1 ring-white/80 hover:bg-white/80",
    FOCUS_RING,
  );

  const inner: ReactNode = (
    <>
      <span className="mt-1.5 flex h-2.5 w-2.5 shrink-0">
        {isUnread && (
          <>
            <span className="h-2.5 w-2.5 rounded-full bg-sky-500" aria-hidden />
            <span className="sr-only">Unread</span>
          </>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-3">
          <span
            className={cn(
              "truncate font-semibold text-neutral-900",
              !isUnread && "font-medium text-neutral-700",
            )}
          >
            {title}
          </span>
          <time dateTime={createdAt} className="shrink-0 text-xs text-neutral-600">
            {formatRelativeTime(createdAt)}
          </time>
        </span>
        <span className="mt-0.5 block text-sm text-neutral-600">{body}</span>
      </span>
    </>
  );

  return (
    <li>
      {linkPath ? (
        <Link href={linkPath} onClick={activate} className={rowClass}>
          {inner}
        </Link>
      ) : (
        <button type="button" onClick={activate} className={rowClass} disabled={!isUnread}>
          {inner}
        </button>
      )}
    </li>
  );
}
